<template>
    <b-modal
        id="student-internship-apply-modal"
        v-model="modal"
        v-if="internship"
        modal-class="apprentice-default-modal internship-detail-modal"
        ref="student-internship-apply-modal"
        size="lg"
        :title="internship.name"
        :ok-title="okTitle"
        :ok-disabled="full"
        cancel-variant="outline-primary"
        :cancel-title="$t('common.cancel')"
        @ok="signUp">
        <template #modal-header="{ close }">
            <div class="header">
                <img
                    :src="
                        internship.file
                            ? internship.file.public_path
                            : 'https://apprentice-portalxm.s3.eu-central-1.amazonaws.com/production/general/course-stage.jpg'
                    "
                    :alt="`Banner ${internship.name}`" />
                <b-button
                    class="btn-times btn-close"
                    variant="primary-icon-only"
                    @click="close()">
                </b-button>
            </div>
        </template>

        <div class="internship-details">
            <h3 class="title">{{ internship.name }}</h3>

            <div class="content">
                <div class="additional-info">
                    <div
                        class="internship-type"
                        v-if="internship.internship_tags.length">
                        <span class="label">{{ $t('internship.internship_type') }}</span>
                        <div class="tags-list">
                            <b-badge
                                class="tag"
                                v-for="tag in internship.internship_tags"
                                pill
                                variant="primary"
                                :key="tag.id">
                                {{ tag.name }}
                            </b-badge>
                        </div>
                    </div>

                    <div class="internship-seats">
                        <span class="label">{{ $t('internship.amount_of_places') }}</span>
                        <span class="placements">
                            <i class="fas fa-users icon"></i>
                            {{ internship.placements }}
                        </span>
                    </div>

                    <div
                        class="internship-location"
                        v-if="internship.location">
                        <span class="label">Locatie:</span>

                        <span class="location">
                            <i class="fas fa-map-marker-alt icon"></i>
                            {{ internship.location }}
                        </span>
                        <span
                            class="country"
                            v-if="internship.country">
                            {{ internship.country }}
                        </span>
                    </div>

                    <div
                        class="internship-email"
                        v-if="internship.email">
                        <span class="label">E-mail adres:</span>
                        <a :href="`mailto:${internship.email}`">{{ internship.email }}</a>
                    </div>

                    <div
                        class="internship-phone-number"
                        v-if="internship.phone">
                        <span class="label">Telefoonnummer:</span>
                        <a :href="`tel:${internship.phone}`">{{ internship.phone }}</a>
                    </div>
                </div>

                <design-2021-apprentice-map-component
                    class="internship-detail-google-map"
                    :title="internship.name"
                    :address="getInternshipAddress()"
                    :google-map-api="googleMapApi">
                </design-2021-apprentice-map-component>
            </div>

            <div class="internship-detail-description">
                <h4 class="internship-detail-description-title">{{ $t('common.phone_number') }}</h4>
                <design2021-apprentice-display-html-component v-html="internship.description">
                </design2021-apprentice-display-html-component>
            </div>

            <div
                class="motivation"
                v-if="!registered">
                <div
                    class="errors"
                    v-if="hasError">
                    <b-alert
                        variant="danger"
                        show>
                        {{ $t('internship.motivation_is_a_mandatory_field') }}
                    </b-alert>
                </div>
                <b-form-group
                    class="internship-rejection-letter"
                    label-for="internship-motivation-letter-input"
                    :label="$t('internship.motivation')">
                    <template v-slot:label>
                        {{ $t('internship.motivation') }} <span class="required">*</span>
                    </template>
                    <b-form-textarea
                        class="apprentice-textarea"
                        id="internship-motivation-letter"
                        v-model="motivationLetter"
                        :placeholder="$t('internship.i_would_like_to_participate_in_this_internship_because')"
                        rows="3"
                        max-rows="6">
                    </b-form-textarea>
                </b-form-group>
            </div>
        </div>
    </b-modal>
</template>

<script>
import Design2021ApprenticeDisplayHtmlComponent from '../../../common/design-2021-apprentice-display-html-component.vue';

export default {
    name: 'design-2021-internship-student-motivation-modal-component',
    components: { Design2021ApprenticeDisplayHtmlComponent },
    props: {
        googleMapApi: {
            type: String,
            required: true,
        },
    },
    emits: ['add_internship'],
    data() {
        return {
            projectId: 0,
            motivationLetter: '',
            modal: false,
            internship: null,
            registered: false,
            hasError: false,
        };
    },
    computed: {
        okTitle() {
            if (this.registered) {
                return this.$t('internship.unsubscribe');
            }
            if (!this.full) {
                return this.$t('internship.subscribe');
            }
            return 'Vol';
        },
        full() {
            return this.internship.placements_filled >= this.internship.placements;
        },
    },
    methods: {
        show(internship, projectId, registered = false) {
            this.projectId = projectId;
            this.hasError = false;
            this.motivationLetter = '';
            this.internship = internship;
            this.registered = registered;
            this.modal = true;
        },

        signUp(bvModal) {
            this.hasError = false;

            if (this.motivationLetter === '') {
                this.hasError = true;
                bvModal.preventDefault();
                return;
            }

            this.axios
                .post(this.routes.studentInternshipSignup(this.$authUser.id), {
                    internshipId: this.internship.id,
                    projectId: this.projectId,
                    motivationLetter: this.motivationLetter,
                })
                .then(
                    (response) => {
                        this.$emit('signed-internship', response.data);
                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
        },

        getOwnerTypeClass(model) {
            switch (model) {
                case 'App\\Models\\User':
                    return 'fad fa-user-crown';

                case 'App\\Models\\Organization\\Organization':
                case 'App\\Models\\School':
                    return 'fad fa-university';
            }
        },

        getOwnerTypeText(model) {
            switch (model) {
                case 'App\\Models\\User':
                    return this.$t('internship.personal_internship');

                case 'App\\Models\\Organization\\Organization':
                    return this.$t('internship.external_internship');

                case 'App\\Models\\School':
                    return this.$t('internship.schools_internship');
            }
        },

        getInternshipAddress() {
            return {
                street: this.internship.street,
                number: this.internship.streetnumber,
                zipcode: this.internship.zipcode,
                location: this.internship.location,
            };
        },
    },
};
</script>

<style scoped lang="scss">
#student-internship-apply-modal___BV_modal_outer_ ::v-deep {
    .modal-dialog {
        .modal-content {
            .modal-header {
                position: relative;
                padding: 0;
                height: 175px;
                border-top-right-radius: $border-radius-content;
                border-top-left-radius: $border-radius-content;

                .btn-close {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    font-size: 1.5625rem;

                    &:before {
                        margin-right: 0;
                        font-weight: $font-weight-normal;
                    }
                }
            }
        }
    }
}

.header {
    width: 100%;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
}

.header {
    height: 100%;
}

.title {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.additional-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    > div {
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        column-gap: map-get($grid-gutter-spacing, 'twenty-one');
        width: 100%;

        .label {
            margin: 0 0 map-get($grid-gutter-spacing, 'eight') 0;
            min-width: 130px;
            align-self: flex-start;
        }

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

.tags-list {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    row-gap: map-get($grid-gutter-spacing, 'five');
    column-gap: map-get($grid-gutter-spacing, 'eigh');

    .tag {
        padding: map-get($grid-gutter-spacing, 'four') map-get($grid-gutter-spacing, 'eight');
        font-size: 0.8125rem;
    }
}

.additional-info {
    .icon {
        color: $primary;
    }
}

.content,
.internship-detail-description {
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
}

.description {
    color: $text-muted-color;
    line-height: 1.3;
    font-size: $base-font-size;
    margin-top: map-get($grid-gutter-spacing, 'ten');
    margin-bottom: $grid-gutter-width;
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .additional-info {
        row-gap: map-get($grid-gutter-spacing, 'eight');

        > div {
            flex-direction: row;
            margin-bottom: 0;
            align-items: center;

            .label {
                margin: 0;
                align-self: center;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .content {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: flex-start;
        column-gap: $grid-gutter-width;
    }

    .additional-info {
        width: 45%;

        > div:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .internship-detail-google-map {
        width: calc(55% - 30px);
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .additional-info {
        > div {
            .motivation,
            .internship-details-owner,
            .email,
            .address,
            .status,
            .tags-list,
            .phone-number {
                max-width: calc(100% - 151px);
            }
        }
    }
}
</style>
